export type ActionKind = 'approve' | 'reject' | 'publish' | 'publishAndStay' | 'quickPublish';

export type PostAction = 'redirectToBackLink' | 'nextDoc' | 'stay';

export interface DecideNavigationInput {
  action: ActionKind;
  isToApproveCategory: boolean;
  isBkRoleUser: boolean;
  // True iff the user can drive the financial publish flow on the open
  // document: full / Restricted BK on a financial doc (has a Publish CTA).
  // Limited / RO BK and general docs are excluded. See
  // selectors.isPublishFlowActiveSelector.
  isPublishFlowActive: boolean;
  // True iff the current user has approved the open document (their signature
  // is no longer pending). Synchronous and reliable: the approve saga updates
  // the store before the navigation decision runs. See
  // selectors.isCurrentDocumentApprovedByUserSelector.
  isCurrentDocApproved: boolean;
  // True iff the open document is published (`_S_ACCEPTED`). On production
  // `acceptDocument` is synchronous: the accept saga dispatches
  // `documentUpdate.success` (refreshing the store) before the navigation
  // decision runs, so after a regular Publish this is fresh at decision time —
  // no WS round-trip. Quick publish never lands `_S_ACCEPTED` synchronously, so
  // `isNavigatingAction` advances it via `isCurrentDocJustPublished` / the
  // `quickPublish` action instead of this flag. See isCurrentDocumentPublishedSelector.
  isCurrentDocPublished: boolean;
  // True iff the open document was just published/scheduled this session —
  // `justPublished` is non-null (reset to null on every doc open). Lets approve
  // advance once a doc the BK already quick-published gets approved (its
  // `_S_ACCEPTED` is async and not in the store yet). See justPublishedDocumentSelector.
  isCurrentDocJustPublished: boolean;
  hasNextDoc: boolean;
  // True iff the user has a workspace back URL. Required for any redirect:
  //   - Well Done override (came from "To approve") — the back link will
  //     point to that "To approve" workspace because `setNavigationAction`
  //     and `saveBackSearchUrlAction` are dispatched together with the same
  //     params; `isToApproveCategory=true` ⇒ back link's `category1=46`.
  //   - Inbox publish-last fallback — back to whichever workspace the user
  //     came from (mirrors dev `nextDocument(backAction)`).
  hasBackLink: boolean;
  // Publish process done for the whole list: every financial doc is published
  // (`accepted`) OR rejected, so none is awaiting publish. INDEPENDENT of
  // approval — a doc can be published without being approved and vice versa.
  // General docs are skipped (no publish flow). See
  // selectors.allFinancialDocsPublishedOrRejectedSelector.
  allFinancialDocsPublishedOrRejected: boolean;
  // True iff the user has no documents waiting for their approve.
  isPersonalApprovalQueueEmpty: boolean;
}

const isApprovalAction = (action: ActionKind): boolean => action === 'approve' || action === 'reject';

const isPublishAction = (action: ActionKind): boolean =>
  action === 'publish' || action === 'quickPublish';

// Whether the action advances off the current document.
//
// "To approve" + publishing BK (full / Restricted BK on a financial doc) —
// DA-19047 ("Keep BK on document via Approve, redirect on next upon Publish").
// Advance only when the open doc is DONE for this BK:
//   - reject → next (redirects for all roles);
//   - otherwise → next once the doc is approved AND publish-attempted (published,
//     just quick-published this session, or the current action is quick publish);
//   - approve on a not-yet-published doc → KEEP (the BK stays to publish it);
//   - publishAndStay → keep.
// All other roles / categories keep the pre-DA-19047 behavior.
const isNavigatingAction = ({
  action,
  isToApproveCategory,
  isBkRoleUser,
  isPublishFlowActive,
  isCurrentDocApproved,
  isCurrentDocPublished,
  isCurrentDocJustPublished,
}: DecideNavigationInput): boolean => {
  if (action === 'publishAndStay') return false;

  if (isToApproveCategory) {
    if (isPublishFlowActive) {
      if (action === 'reject') return true; // reject → next (all roles)
      // Advance once the doc is DONE = approved AND publish-attempted: synchronously
      // published (`_S_ACCEPTED`), already quick-published this session
      // (`isCurrentDocJustPublished`), or the current action is quick publish (its
      // result isn't readable yet at decision time). Otherwise keep the BK on the doc.
      return (
        isCurrentDocApproved &&
        (isCurrentDocPublished || isCurrentDocJustPublished || action === 'quickPublish')
      );
    }
    // Non-publishing roles / general docs: approve/reject advance (any role),
    // publish-like keeps the user on the doc.
    return isApprovalAction(action);
  }

  // Inbox/Other (unchanged):
  //   approve/reject → STAY only when the user is in the financial publish
  //     flow on this doc (full / Restricted BK on a financial doc — they still
  //     need to publish it). All other cases navigate: client always, BK on a
  //     general doc (DA-18889), Limited / RO BK regardless of doctype.
  //   publish/Force/Quick publish → navigate to next (no-next falls back to
  //     the back link below).
  if (isApprovalAction(action)) return !(isBkRoleUser && isPublishFlowActive);
  return true;
};

export const decideNavigation = (input: DecideNavigationInput): PostAction => {
  const {
    action,
    isToApproveCategory,
    isPublishFlowActive,
    hasNextDoc,
    hasBackLink,
    allFinancialDocsPublishedOrRejected,
    isPersonalApprovalQueueEmpty,
  } = input;

  // publishAndStay is an explicit user choice — never override with redirect.
  if (action === 'publishAndStay') return 'stay';

  // "Well Done" override — redirect to the back link when the BK's "To approve"
  // work is done. Gated on:
  //   - approval side: `isPersonalApprovalQueueEmpty` (nothing left to approve/reject), AND
  //   - publish side (publishing BK only): `allFinancialDocsPublishedOrRejected` —
  //     every financial doc on the list carries `_S_ACCEPTED` or `_S_APPROVAL_REJECTED`
  //     (a rejected doc STAYS in the list and counts via its tag; a doc just
  //     quick-published counts via the `justPublished` marker), OR the current action
  //     is `quickPublish` (its `_S_ACCEPTED` never lands synchronously, so gating the
  //     last-doc redirect on it would strand the user — DA-18891).
  // For non-publishing roles / general docs the publish side doesn't apply
  // (`!isPublishFlowActive`), so an empty queue alone signals "done" (requiring an
  // `accepted` flag they can never produce stranded them — DA-18886 / DA-18888).
  // (The ticket phrases this as "Well Done only if all Approved AND Published";
  // general docs and rejected docs are the carve-outs.)
  const allWorkDone =
    isPersonalApprovalQueueEmpty &&
    (!isPublishFlowActive || action === 'quickPublish' || allFinancialDocsPublishedOrRejected);
  if (isToApproveCategory && allWorkDone && hasBackLink) {
    return 'redirectToBackLink';
  }

  if (!isNavigatingAction(input)) return 'stay';

  if (hasNextDoc) return 'nextDoc';

  // No next doc available. For publish-related actions in Inbox preserve the
  // dev `nextDocument(backAction)` fallback by navigating to the back URL.
  // approve/reject and "To approve" actions never redirect through this
  // fallback — they stay until the Well Done override above fires.
  if (!isToApproveCategory && isPublishAction(action) && hasBackLink) {
    return 'redirectToBackLink';
  }

  return 'stay';
};
